import type { AuditEntryDto } from '@/lib/dto'
import type { StatusTone } from '@/ui/primitives/StatusDot'

/**
 * Словарь действий аудита: сырой код → человеческое название и группа.
 *
 * Зачем вообще словарь. В `AuditLog.action` лежат машинные коды вида
 * `auth.login.success` — они удобны для грепа и совершенно нечитаемы в таблице,
 * которую смотрят, когда что-то случилось. Но и выбрасывать код нельзя: именно
 * его ищут в исходниках и в логах сервера. Поэтому экран показывает ОБА:
 * название крупно, код рядом мелким моно-шрифтом.
 *
 * Незнакомый код не считается ошибкой: сервер может ввести новое действие
 * раньше, чем обновится фронт (та же логика, что у `bot.event.kind` в
 * `@apiary/shared`). Такой код показывается как есть и попадает в группу
 * «Прочее» — молча пропасть из таблицы аудита он не имеет права.
 */

export interface AuditActionMeta {
  label: string
  group: string
  /** Тон строки: `fail` — отказ, `warn` — разрушающее или подозрительное. */
  tone?: StatusTone
}

const GROUP = {
  auth: 'Аутентификация',
  access: 'Доступ и участники',
  bots: 'Боты',
  accounts: 'MC-аккаунты',
  servers: 'Серверы',
  viewer: 'Камера',
  infra: 'Инфраструктура',
  other: 'Прочее'
} as const

export const AUDIT_ACTIONS: Record<string, AuditActionMeta> = {
  'auth.login.success': { label: 'Вход выполнен', group: GROUP.auth },
  'auth.login.failure': { label: 'Неудачная попытка входа', group: GROUP.auth, tone: 'fail' },
  'auth.login.lockout': {
    label: 'Вход заблокирован после попыток',
    group: GROUP.auth,
    tone: 'warn'
  },
  'auth.refresh.reuse': {
    label: 'Повторно использован refresh-токен',
    group: GROUP.auth,
    tone: 'fail'
  },
  'auth.logout': { label: 'Выход из аккаунта', group: GROUP.auth },
  'auth.password.change': { label: 'Пароль изменён', group: GROUP.auth, tone: 'warn' },
  'auth.register': { label: 'Регистрация пользователя', group: GROUP.auth },
  'auth.profile.update': { label: 'Профиль изменён', group: GROUP.auth },
  'auth.totp.setup': { label: 'Начата настройка двухфакторки', group: GROUP.auth },
  'auth.totp.enable': { label: 'Двухфакторка включена', group: GROUP.auth },
  'auth.totp.disable': { label: 'Двухфакторка выключена', group: GROUP.auth, tone: 'warn' },
  'auth.totp.recovery': {
    label: 'Вход по резервному коду',
    group: GROUP.auth,
    // Резервный код одноразовый и гасит 2FA целиком — это всегда повод посмотреть.
    tone: 'warn'
  },

  'user.invite.create': { label: 'Приглашение создано', group: GROUP.access },
  'user.invite.accept': { label: 'Приглашение принято', group: GROUP.access },
  'user.invite.revoke': { label: 'Приглашение отозвано', group: GROUP.access, tone: 'warn' },
  'member.role.change': { label: 'Роль участника изменена', group: GROUP.access, tone: 'warn' },
  'member.remove': { label: 'Участник удалён', group: GROUP.access, tone: 'warn' },
  'org.limits.change': { label: 'Лимиты организации изменены', group: GROUP.access, tone: 'warn' },

  'server.create': { label: 'Профиль сервера создан', group: GROUP.servers },
  'server.update': { label: 'Профиль сервера изменён', group: GROUP.servers },
  'server.delete': { label: 'Профиль сервера удалён', group: GROUP.servers, tone: 'warn' },

  'mcAccount.create': { label: 'MC-аккаунт добавлен', group: GROUP.accounts },
  'mcAccount.update': { label: 'MC-аккаунт изменён', group: GROUP.accounts },
  'mcAccount.delete': { label: 'MC-аккаунт удалён', group: GROUP.accounts, tone: 'warn' },
  'mcAccount.auth.begin': { label: 'Начат вход в Microsoft', group: GROUP.accounts },
  'mcAccount.auth.ok': { label: 'Вход в Microsoft удался', group: GROUP.accounts },
  'mcAccount.auth.fail': {
    label: 'Вход в Microsoft не удался',
    group: GROUP.accounts,
    tone: 'fail'
  },

  'bot.create': { label: 'Бот создан', group: GROUP.bots },
  'bot.update': { label: 'Настройки бота изменены', group: GROUP.bots },
  'bot.delete': { label: 'Бот удалён', group: GROUP.bots, tone: 'warn' },
  'bot.start': { label: 'Бот запущен', group: GROUP.bots },
  'bot.stop': { label: 'Бот остановлен', group: GROUP.bots },
  'bot.restart': { label: 'Бот перезапущен', group: GROUP.bots },
  'bot.chat': { label: 'Сообщение в чат', group: GROUP.bots },
  'bot.command': { label: 'Серверная команда', group: GROUP.bots, tone: 'warn' },
  'bot.bulk': { label: 'Массовое действие с ботами', group: GROUP.bots },

  'runner.register': { label: 'Runner подключился', group: GROUP.infra },
  'runner.offline': { label: 'Runner отключился', group: GROUP.infra, tone: 'warn' },
  'events.rotate': { label: 'Ротация логов', group: GROUP.infra },
  'alert.create': { label: 'Правило алерта создано', group: GROUP.infra },
  'alert.update': { label: 'Правило алерта изменено', group: GROUP.infra },
  'alert.delete': { label: 'Правило алерта удалено', group: GROUP.infra, tone: 'warn' },
  'alert.test': { label: 'Проверка доставки алерта', group: GROUP.infra },
  'alert.fire': { label: 'Алерт сработал', group: GROUP.infra, tone: 'warn' },

  'viewer.open': { label: 'Камера открыта', group: GROUP.viewer },
  'viewer.close': { label: 'Камера закрыта', group: GROUP.viewer }
}

/** Человеческое название действия. Незнакомый код возвращается как есть. */
export function auditActionLabel(action: string): string {
  return AUDIT_ACTIONS[action]?.label ?? action
}

export function auditActionGroup(action: string): string {
  return AUDIT_ACTIONS[action]?.group ?? GROUP.other
}

export function auditActionTone(action: string): StatusTone | null {
  return AUDIT_ACTIONS[action]?.tone ?? null
}

export function isKnownAuditAction(action: string): boolean {
  return AUDIT_ACTIONS[action] !== undefined
}

/**
 * Варианты для фильтра действий. Порядок — по группам, внутри группы по
 * алфавиту: список из тридцати кодов, отсортированный «как в объекте», искать
 * глазами невозможно. Название группы приписано к подписи, потому что примитив
 * `Select` не умеет заголовков секций, а `note` занят сырым кодом.
 */
export function auditActionOptions(
  /** Коды, встретившиеся в выдаче: незнакомые серверные действия тоже должны быть в фильтре. */
  seen: readonly string[] = []
): { value: string; label: string; note?: string }[] {
  const codes = [...new Set([...Object.keys(AUDIT_ACTIONS), ...seen])]
  const sorted = codes.sort((left, right) => {
    const byGroup = auditActionGroup(left).localeCompare(auditActionGroup(right), 'ru')
    if (byGroup !== 0) return byGroup
    return auditActionLabel(left).localeCompare(auditActionLabel(right), 'ru')
  })
  return [
    { value: '', label: 'Все действия' },
    ...sorted.map(code => ({
      value: code,
      label: `${auditActionGroup(code)} · ${auditActionLabel(code)}`,
      note: code
    }))
  ]
}

// ─────────────────────────── объект действия ───────────────────────────

const TARGET_TYPES: Record<string, string> = {
  bot: 'Бот',
  server: 'Сервер',
  mcAccount: 'MC-аккаунт',
  member: 'Участник',
  invite: 'Приглашение',
  org: 'Организация',
  runner: 'Runner',
  apiKey: 'API-ключ'
}

export function targetTypeLabel(type: string | null): string {
  if (type === null) return '—'
  return TARGET_TYPES[type] ?? type
}

export function targetTypeOptions(
  seen: readonly (string | null)[] = []
): { value: string; label: string; note?: string }[] {
  const codes = [
    ...new Set([
      ...Object.keys(TARGET_TYPES),
      ...seen.filter((type): type is string => type !== null)
    ])
  ].sort((left, right) => targetTypeLabel(left).localeCompare(targetTypeLabel(right), 'ru'))
  return [
    { value: '', label: 'Любой объект' },
    ...codes.map(code => ({ value: code, label: targetTypeLabel(code), note: code }))
  ]
}

// ─────────────────────────────── актор ───────────────────────────────

export interface AuditActor {
  kind: 'user' | 'apiKey' | 'system'
  /** Значение для фильтра: `u:<id>` / `k:<id>` / пусто у системы. */
  key: string
  /** Что показать в таблице и в списке фильтра. */
  label: string
  /** Вторая строка: e-mail пользователя или пометка «API-ключ». */
  note: string | null
}

/**
 * Кто совершил действие. Три случая, и все три встречаются на живой панели:
 * человек, API-ключ автоматизации и сама система (джоба retention пишет аудит
 * без актора). Различать их обязательно — «неизвестно кто» в журнале
 * безопасности бесполезно.
 */
export function auditActor(entry: AuditEntryDto): AuditActor {
  if (entry.actorUserId !== null) {
    return {
      kind: 'user',
      key: `u:${entry.actorUserId}`,
      label: entry.actorUserName ?? `Пользователь ${entry.actorUserId}`,
      note: entry.actorUserEmail
    }
  }
  if (entry.actorApiKeyId !== null) {
    return {
      kind: 'apiKey',
      key: `k:${entry.actorApiKeyId}`,
      label: entry.actorApiKeyName ?? `Ключ ${entry.actorApiKeyId}`,
      note: 'API-ключ'
    }
  }
  return { kind: 'system', key: '', label: 'Система', note: 'без актора' }
}

/** Разбор значения фильтра актора обратно в параметры запроса. */
export function actorFilterToQuery(value: string): {
  actorUserId?: string
  actorApiKeyId?: string
} {
  if (value.startsWith('u:')) return { actorUserId: value.slice(2) }
  if (value.startsWith('k:')) return { actorApiKeyId: value.slice(2) }
  return {}
}

/** Список акторов для фильтра — собирается из загруженных страниц. */
export function actorOptions(
  entries: readonly AuditEntryDto[]
): { value: string; label: string; note?: string }[] {
  const found = new Map<string, AuditActor>()
  for (const entry of entries) {
    const actor = auditActor(entry)
    if (actor.key !== '' && !found.has(actor.key)) found.set(actor.key, actor)
  }
  return [
    { value: '', label: 'Любой актор' },
    ...[...found.values()]
      .sort((left, right) => left.label.localeCompare(right.label, 'ru'))
      .map(actor => ({
        value: actor.key,
        label: actor.label,
        note: actor.kind === 'apiKey' ? 'ключ' : 'юзер'
      }))
  ]
}
